import { CSSProperties, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

/**
 * CLE-2895 — a small burst when a worklist cluster is hidden ("Hide this cluster").
 *
 * Ani asked for confetti; this is the tasteful reading of that, in the spirit of the HST import's
 * dragon-fire shimmer: twenty tiny particles scatter from the confirm button and fade in under a
 * second. It is rendered in a portal on `document.body` with `pointer-events: none`, so it never
 * blocks the dialog closing or the row leaving the list, and it outlives the dialog.
 *
 * Reduced motion: no particles — a small static "Hidden ✓" tag at the same spot instead, for a
 * little longer, then gone.
 */

export const CELEBRATION_MS = 900;
export const REDUCED_MOTION_CELEBRATION_MS = 1400;

const PARTICLE_COUNT = 20;
const COLORS = [
  "#16a34a",
  "#22c55e",
  "#0ea5e9",
  "#6366f1",
  "#f59e0b",
  "#ec4899",
];

export interface ICelebrationOrigin {
  x: number;
  y: number;
}

export const prefersReducedMotion = (): boolean => {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
};

const scatter = keyframes`
  0% {
    transform: translate(-50%, -50%) rotate(0deg) scale(1);
    opacity: 1;
  }
  70% {
    opacity: 1;
  }
  100% {
    transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)) scale(0.6);
    opacity: 0;
  }
`;

const Layer = styled.div`
  position: fixed;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 3000;
`;

const Particle = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 2px;
  opacity: 0;
  animation: ${scatter} ${CELEBRATION_MS}ms cubic-bezier(0.2, 0.7, 0.3, 1)
    forwards;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const MarkedTag = styled.span`
  position: absolute;
  transform: translate(-50%, -150%);
  white-space: nowrap;
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  background: var(--green-50, #f0fdf4);
  color: var(--green-800, #166534);
  border: 1px solid var(--green-200, #bbf7d0);
`;

/**
 * Each particle's direction and distance, spread evenly round the circle with a little variation by
 * index — deterministic, so the burst looks the same every time and a test can rely on it.
 */
const particles = Array.from({ length: PARTICLE_COUNT }, (_, index) => {
  const angle = (index / PARTICLE_COUNT) * 2 * Math.PI + (index % 3) * 0.12;
  const distance = 38 + (index % 4) * 11;
  // Slightly upward: a burst that falls straight onto the table below reads as clutter.
  const dx = Math.round(Math.cos(angle) * distance);
  const dy = Math.round(Math.sin(angle) * distance * 0.8 - 12);
  return {
    dx,
    dy,
    rot: (index % 2 === 0 ? 1 : -1) * (120 + index * 17),
    size: index % 3 === 0 ? 7 : 5,
    color: COLORS[index % COLORS.length],
    delay: (index % 5) * 18,
  };
});

export function WorklistCelebration({
  origin,
  onDone,
}: {
  origin: ICelebrationOrigin;
  onDone: () => void;
}) {
  const reduced = prefersReducedMotion();

  useEffect(() => {
    const timer = window.setTimeout(
      onDone,
      reduced ? REDUCED_MOTION_CELEBRATION_MS : CELEBRATION_MS + 150
    );
    return () => window.clearTimeout(timer);
  }, [onDone, reduced]);

  return createPortal(
    <Layer
      style={{ left: origin.x, top: origin.y }}
      data-testid="benefitWorklist.dismiss.celebration"
      aria-hidden={!reduced}
    >
      {reduced ? (
        <MarkedTag role="status" data-testid="benefitWorklist.dismiss.marked">
          Hidden ✓
        </MarkedTag>
      ) : (
        particles.map((particle, index) => (
          <Particle
            // A fixed list, never reordered.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            data-testid="benefitWorklist.dismiss.particle"
            style={
              {
                width: particle.size,
                height: particle.size,
                background: particle.color,
                animationDelay: `${particle.delay}ms`,
                "--dx": `${particle.dx}px`,
                "--dy": `${particle.dy}px`,
                "--rot": `${particle.rot}deg`,
              } as CSSProperties
            }
          />
        ))
      )}
    </Layer>,
    document.body
  );
}
